import React, { useEffect } from 'react';
import { useExperiments } from '@wix/wix-experiments-react';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { type OnClick } from '../../../types';
import { type ApplePayCreditCardNetworks } from '../../../types/PaymentMethods';
import { type TopProps } from '../../../types/WidgetProps';
import { type StartApplePayFlowParams } from '../../../actions/payment/startApplePayFlow';
import { useBiLogger } from '../../Provider/BiLogger/BiLoggerProvider';
import { useFedopsLogger } from '../../Provider/Fedops/FedopsProvider';
import { getPageFingerprint } from '../../../utils/sentry/getPageFingerprint';
import { amountToLong } from '../../../utils/amountToLong';
import { decorateGetInitialPaymentDataWithBi } from '../../../services/applePay/decorateGetInitialPaymentDataWithBi';
import { applePayButtonRendered, expressCheckoutWidgetButtonClicked } from '../../../utils/bi/events/applePay';
import { ApplePayView } from './view';

type Props = {
  sessionId: string;
  countryCode: string;
  merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[];
  supportedNetworks: ApplePayCreditCardNetworks[];
  color: 'white' | 'black';
  topProps: TopProps;
  onClick: OnClick;
  startApplePayFlow: (params: StartApplePayFlowParams) => void;
};

export const ApplePayController = (props: Props) => {
  const { topProps, color, sessionId, startApplePayFlow } = props;
  const { experiments } = useExperiments();
  const biLogger = useBiLogger();
  const fedopsLogger = useFedopsLogger();

  useEffect(() => {
    const { meta, requestShipping, currency, paymentAmount } = topProps;

    biLogger.report(applePayButtonRendered, {
      checkoutType: 'express',
      pageType: getPageFingerprint(),
      cartId: meta.cartId,
      paymentmethod: PaymentMethod.ApplePay,
      paymentProvider: PaymentProvider.WixPayUS,
      currency,
      amount: amountToLong(paymentAmount),
      // @ts-expect-error default value override
      requestShipping,
      msid: meta.siteId,
      visitorSessionId: meta.visitorId,
    });
  }, []);

  const onClick = () => {
    const {
      paymentAmount,
      paymentLabel,
      currency,
      requestShipping,
      shippingMethods,
      paymentBreakdown,
      meta,
      domain,
      onShippingContactSelected,
      onShippingMethodSelected,
      onPaymentAuthorized,
      getInitialPaymentData,
    } = topProps;

    biLogger.report(expressCheckoutWidgetButtonClicked, {
      checkoutType: 'express',
      amountLong: amountToLong(paymentAmount),
      amount: amountToLong(paymentAmount),
      appInstanceId: meta.appInstanceId,
      currency,
      paymentProvider: PaymentProvider.WixPayUS,
      paymentmethod: PaymentMethod.ApplePay,
      productId: meta.productId,
      isPartner: undefined as any, // TODO[bi]
      orderSnapshotId: undefined as any, // TODO[bi]
      transactionId: undefined as any, // TODO[bi]
    });

    startApplePayFlow({
      ...props,
      experiments,
      biLogger,
      fedopsLogger,
      meta,
      sessionId,
      paymentAmount,
      paymentLabel,
      currencyCode: currency,
      requestShipping,
      shippingMethods,
      paymentBreakdown,
      domain,
      onShippingContactSelected,
      onShippingMethodSelected,
      onPaymentAuthorized,
      getInitialPaymentDataWithBi: decorateGetInitialPaymentDataWithBi({
        biLogger,
        cb: getInitialPaymentData,
        data: { appInstanceId: meta.appInstanceId, currencyCode: currency, paymentLabel },
      }),
    });
  };

  return (
    <ApplePayView
      height={topProps.buttonStyle.height}
      locale={topProps.locale}
      shape={topProps.buttonStyle.shape}
      color={color}
      onClick={onClick}
    />
  );
};
